import React, { useEffect, useRef } from "react";

/**
 * Burst de confetti sin dependencias: canvas fixed a pantalla completa que se dibuja
 * solo mientras dura la animacion y despues se desmonta el efecto.
 * Se monta cuando el envio de la actualizacion de datos fue exitoso.
 */

const COLORES = ["#e3001b", "#ff5a5f", "#1a1c1e", "#f5b301", "#00a6a6", "#7c3aed"];
const CANTIDAD = 160;
const DURACION_MS = 2600;
const GRAVEDAD = 0.16;

interface Particula {
    x: number;
    y: number;
    vx: number;
    vy: number;
    rot: number;
    vrot: number;
    ancho: number;
    alto: number;
    color: string;
}

export const Confetti: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const prefiereReducir = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        if (prefiereReducir) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const ancho = window.innerWidth;
        const alto = window.innerHeight;
        canvas.width = ancho * dpr;
        canvas.height = alto * dpr;
        ctx.scale(dpr, dpr);

        const particulas: Particula[] = Array.from({ length: CANTIDAD }, () => {
            const desdeIzquierda = Math.random() < 0.5;
            return {
                x: desdeIzquierda ? 0 : ancho,
                y: alto * (0.35 + Math.random() * 0.2),
                vx: (desdeIzquierda ? 1 : -1) * (4 + Math.random() * 7),
                vy: -(6 + Math.random() * 9),
                rot: Math.random() * Math.PI,
                vrot: (Math.random() - 0.5) * 0.3,
                ancho: 6 + Math.random() * 6,
                alto: 4 + Math.random() * 5,
                color: COLORES[Math.floor(Math.random() * COLORES.length)],
            };
        });

        const inicio = performance.now();
        let rafId = 0;

        const tick = (ahora: number) => {
            const transcurrido = ahora - inicio;
            ctx.clearRect(0, 0, ancho, alto);

            for (const p of particulas) {
                p.vy += GRAVEDAD;
                p.x += p.vx;
                p.y += p.vy;
                p.vx *= 0.99;
                p.rot += p.vrot;

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rot);
                ctx.globalAlpha = Math.max(0, 1 - transcurrido / DURACION_MS);
                ctx.fillStyle = p.color;
                ctx.fillRect(-p.ancho / 2, -p.alto / 2, p.ancho, p.alto);
                ctx.restore();
            }

            if (transcurrido < DURACION_MS) {
                rafId = requestAnimationFrame(tick);
            } else {
                ctx.clearRect(0, 0, ancho, alto);
            }
        };

        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden
            style={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 1400,
            }}
        />
    );
};
